import { useCallback, useEffect, useRef } from 'react'

export type LiveExtractionTrigger = 'paste' | 'typing' | 'blur' | 'generate'

export function extractionDelayForChange(trigger: 'paste' | 'typing', text: string): number {
  if (trigger === 'paste') return 600
  return /(?:[.!?]\s*|\n)$/.test(text) ? 1800 : 3500
}

function nothingToClear(text: string, acceptedText: string): boolean {
  return !text.trim() && !acceptedText.trim()
}

interface UseLiveHpoExtractionOptions {
  enabled: boolean
  buttonOnly: boolean
  text: string
  acceptedText: string
  /**
   * Starts an extraction; the hook does not wait for one to finish before starting the next, so
   * `onExtract` must cancel or ignore the one it supersedes. Blank text is passed too, once the accepted
   * text is not blank, and finds nothing without asking the service.
   */
  onExtract: (text: string, trigger: LiveExtractionTrigger) => void | Promise<void>
}

export function useLiveHpoExtraction({
  enabled,
  buttonOnly,
  text,
  acceptedText,
  onExtract,
}: UseLiveHpoExtractionOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const runRef = useRef(0)
  const inFlightTextRef = useRef<string | null>(null)
  const latestTextRef = useRef(text)
  const acceptedTextRef = useRef(acceptedText)
  latestTextRef.current = text
  acceptedTextRef.current = acceptedText

  const cancelScheduled = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => cancelScheduled, [cancelScheduled])

  const run = useCallback(
    (nextText: string, trigger: LiveExtractionTrigger) => {
      const id = ++runRef.current
      inFlightTextRef.current = nextText
      void Promise.resolve(onExtract(nextText, trigger)).finally(() => {
        if (runRef.current === id) inFlightTextRef.current = null
      })
    },
    [onExtract]
  )

  const runNow = useCallback(
    (trigger: 'blur' | 'generate') => {
      cancelScheduled()
      if (!enabled || nothingToClear(latestTextRef.current, acceptedTextRef.current)) return
      if (trigger === 'blur' && latestTextRef.current === acceptedTextRef.current) return
      // Clicking anything blurs the field; restarting the request for the same text only delays it.
      if (trigger === 'blur' && latestTextRef.current === inFlightTextRef.current) return
      run(latestTextRef.current, trigger)
    },
    [cancelScheduled, enabled, run]
  )

  const schedule = useCallback(
    (trigger: 'paste' | 'typing', nextText: string) => {
      cancelScheduled()
      if (!enabled || nothingToClear(nextText, acceptedTextRef.current)) return
      // Clearing the note needs no service call, so it applies even when generation is button-only.
      if (buttonOnly && nextText.trim()) return
      if (nextText === acceptedTextRef.current) return
      timerRef.current = setTimeout(
        () => {
          timerRef.current = null
          run(nextText, trigger)
        },
        extractionDelayForChange(trigger, nextText)
      )
    },
    [buttonOnly, cancelScheduled, enabled, run]
  )

  return {
    schedule,
    blur: () => {
      if (!buttonOnly) runNow('blur')
    },
    generate: () => runNow('generate'),
    cancelScheduled,
  }
}
